import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import {  RCCButton, RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi} from "react-common-components";
import {  useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import Tabs from "src/components/Tabs";
import extractSingleValueFromArrayOfObject from "src/utils/extractSingleValueFromArrayOfObject";
import DeleteModal from "src/components/DeleteModal";
import  StandardItensModal  from "src/components/StandardItems";
import { IRefStandardItemsModals } from "src/components/StandardItems/interface";
import { v4 } from "uuid";
import TabGeneral from "./tabGeneral";
import { IComponentAuditModal, IRefModalAuditAddOrChange, IRefTabAuditItems } from "./interface";
import { AuditSituation, IAuditSelect } from "../../interface";
import FormSimplifiedFields from "./formSimplifiedFields";
import { addAndChangeSchemaAudit, deleteSchemaAudit } from "../../schema";
import TabAuditItems from "./tabAuditItems";
import ItemAudited from "../ModalRunAudit/itemAudited";
import Auditors from "../AuditorsAndParticipants/Auditors";
import Participants from "../AuditorsAndParticipants/Participants";

const ModalAudit : React.ForwardRefRenderFunction<IRefModalAuditAddOrChange,IComponentAuditModal> = ({
	onCallbackUpdate,
	onCallbackDelete,
	loadGroupers
}, ref) => {

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();
	const modal = useRef<RCCRefModalProps>(null);
	const refStandardItemsModal = useRef<IRefStandardItemsModals>(null);
	const refTabAuditItems = useRef<IRefTabAuditItems>(null);
	const [formSimplified, setFormSimplified] = useState(true);
	const [ selectedAudit, setSelectedAudit] = useState<IAuditSelect>({} as IAuditSelect);
	const [ standardId, setStandardId] = useState<number | null>(null);
	const [isAdd, setIsAdd] = useState(false);
	const [isFinaly, setIsFinaly] = useState(false);
	const [isCopy, setIsCopy] = useState(false);

	const { handleSubmit, unregister, control, setError, setValue, register, reset, watch, formState: { errors }, clearErrors } = useForm();

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));

	// DELETE
	const modalDelete = useRef<RCCRefModalProps>(null); 
	const { handleSubmit: handleSubmitDelete, register: registerDelete, reset : resetDelete, setError : setErrorDelete, formState: { errors : errorsDelete }, clearErrors: clearErrorsDelete } = useForm();

	const actionButtonDelete = (row : IAuditSelect) => { 
		resetDelete();
		setSelectedAudit(row); 
		modalDelete.current?.openModal();
	};

	const submitDelete = async (data: any) => {
		try{
			clearErrorsDelete();	
			modalDelete.current?.setLoadingButton();

			await deleteSchemaAudit(translate).validate({...data, "adb001TituloConfirmation": selectedAudit.adb001Titulo},{
				abortEarly: false
			});
			
			await RCCServicesApi.deleteRequest({
				method: "Audits/Delete",
				urlParameters: {
					id: selectedAudit.adb001Id,
					title: selectedAudit.adb001Titulo
				}
			}); 

			addToast({
				color: "green",
				description: translate("Auditoria excluída com sucesso"),
				title: translate("Sucesso")
			});
			modalDelete.current?.resetLoadingButton();
			modalDelete.current?.closeModal();

			if(onCallbackDelete)
				onCallbackDelete(selectedAudit.adb001Id);

		}catch(err){
			modalDelete.current?.resetLoadingButton();

			if(getValidationErrors(err, setErrorDelete))
				return;

			standardErrorMessages(err, "Erro ao excluir auditoria", addToast);
		}
		
	};

	const actionButtonAdd = () => {
		reset();
		setFormSimplified(true);
		clearErrors();
		setIsCopy(false);
		setIsAdd(true);
		setSelectedAudit({} as IAuditSelect);
		modal.current?.openModal();
	};

	const actionButtonChange = (auditId: number, isCopyAudit: boolean = false) => {	
		reset();
		setIsCopy(isCopyAudit);
		setFormSimplified(false);
		setIsAdd(false);
		clearErrors();
		queryAuditData(auditId, isCopyAudit);
	};

	const queryAuditData = async (id: number, copy: boolean) => {
		setLoading(true);
		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "Audits/Select",
				urlParameters: {
					id
				}
			});

			const audit = data as IAuditSelect;

			setIsFinaly(copy ? false : audit.adb001Situacao === AuditSituation.FINISHED);
			setSelectedAudit(audit);
			setStandardId(audit.adb001Ada001Id);

			modal.current?.openModal();
			setLoading(false);
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao carregar a auditoria"), addToast);
		}
	};

	const onSubmit = async (data: any) => {
		clearErrors();
		try{
			modal.current?.setLoadingButton();

			const dataChanged = {
				...data,
				adb001Id: selectedAudit.adb001Id,
				adb001Situacao: 1,
				adb001Ada001Id: convertToInt(data.adb001Ada001Id),
				adb001Taa001Id: convertToInt(data.adb001Taa001Id),
				adb001Taa005Id: convertToInt(data.adb001Taa005Id),
				adb001Oca001Id: convertToInt(data.adb001Oca001Id),
				adb001Oca002Id: convertToInt(data.adb001Oca002Id),
				adb001Oca004IdNC: convertToInt(data.adb001Oca004IdNC),
				adb001Oca004IdOB: convertToInt(data.adb001Oca004IdOB),
				adb001Oca004IdOM: convertToInt(data.adb001Oca004IdOM),
				adb001Tags: data.adb001Tags ? extractSingleValueFromArrayOfObject("value", data.adb001Tags): null
			};

			if(typeof data.adb001Efa005Id === "object")
				dataChanged.adb001Efa005Id = data.adb001Efa005Id.value;

			if(typeof data.adb001Efa005Id2 === "object")
				dataChanged.adb001Efa005Id2 = data.adb001Efa005Id2.value;

			if(data.adtbb002 && data.adtbb002.length > 0){
				dataChanged.adtbb002 = data.adtbb002.map((adb002: any) => {
					if(adb002.adb002Efa005Id !== null && typeof adb002.adb002Efa005Id === "object")
						return {
							...adb002,
							adb002Efa005Id: adb002.adb002Efa005Id.value
						};
					return {
						...adb002,
						adb002Efa005Id: adb002.adb002Efa005Id === 0 || adb002.adb002Efa005Id === null ? undefined : adb002.adb002Efa005Id,
						adb002Adb001Id: selectedAudit.adb001Id
					};
				});
			}

			if(data.adtbb003 && data.adtbb003.length > 0){
				dataChanged.adtbb003 = data.adtbb003.map((adb003: any) => {
					if(adb003.adb003Efa005Id !== null && typeof adb003.adb003Efa005Id === "object")
						return {
							...adb003,
							adb003Efa005Id: adb003.adb003Efa005Id.value
						};
					return {
						...adb003,
						adb003Efa005Id: adb003.adb003Efa005Id === 0 || adb003.adb003Efa005Id === null ? undefined : adb003.adb003Efa005Id,
						adb003Adb001Id: selectedAudit.adb001Id
					};
				});
			}

			await addAndChangeSchemaAudit(translate).validate(dataChanged,{
				abortEarly: false
			});

			if(isAdd){
				await RCCServicesApi.postRequest({
					method: "Audits/Insert",
					body: {
						...dataChanged
					}
				});
				
				if(loadGroupers)
					loadGroupers();
				
				addToast({
					color: "green",
					description: translate("Auditoria adicionada com sucesso"),
					title: translate("Sucesso")
				});
			}

			if(!isAdd && !isCopy){
				await RCCServicesApi.postRequest({
					method: "Audits/Update",
					body: {
						...dataChanged
					}
				});

				if(onCallbackUpdate)
					onCallbackUpdate(selectedAudit.adb001Id);

				addToast({
					color: "green",
					description: translate("Auditoria alterada com sucesso"),
					title: translate("Sucesso")
				});
			}

			if(isCopy){
				dataChanged.adb001Dtinireal = null;
				dataChanged.adb001Dtfimreal = null;

				await RCCServicesApi.postRequest({
					method: "Audits/Copy",
					body: {
						...dataChanged
					}
				});

				if(loadGroupers)
					loadGroupers();

				addToast({
					color: "green",
					description: translate("Auditoria copiada com sucesso"),
					title: translate("Sucesso")
				});
			}

			modal.current?.resetLoadingButton();
			modal.current?.closeModal();
		}catch(err: any){
			modal.current?.resetLoadingButton();

			if(getValidationErrors(err, setError)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
			}else{
				standardErrorMessages(err, isAdd ? translate("Erro ao adicionar auditoria") : translate("Erro ao alterar auditoria"), addToast);
			}
		}
	};

	const convertToInt = (obj: any) => {
		if(!obj)
			return undefined;

		if(typeof obj === "object")
			return parseInt(obj.value, 10);

		if(typeof obj === "number")
			return obj;

		return parseInt(obj, 10);
	};

	const convertToString = (obj: any) => {
		if(!obj)
			return undefined;

		if(typeof obj === "object")
			return obj.value;

		if(typeof obj === "number")
			return obj.toString();

		return obj;
	};

	const titleModal = () => {
		if(isAdd) return translate("Adicionar Auditoria");
		if(isCopy) return translate("Copiar Auditoria");
		if(isFinaly) return translate("Visualizar Auditoria");
		return translate("Alterar Auditoria");
	};

	const iconModal = () => {
		if(isAdd) return "plus";
		if(isCopy) return "copy";
		if(isFinaly) return "eye";
		return "pencil";
	};

	const onChangeStandard = (option: { value: string; label: string; }) => {
		setStandardId(Number(option.value));
	};

	const openModalStandardItems = () => {
		if(standardId){
			refStandardItemsModal.current?.actionOpen(standardId);
		}else{
			addToast({
				color: "yellow",
				description: translate("Você deve selecionar uma norma primeiro"),
				title: translate("Atenção")
			});
		}
	};

	return (
		<>
			<Modal
				ref={modal} 
				header={{
					title: titleModal(),
					icon: {
						name: iconModal()
					},
					subtitle: !isAdd ? `${translate("Auditoria")}: ${selectedAudit.fullTitle}` : ""
				}}
				onSubmit={handleSubmit(onSubmit)}
				footer={{
					showSaveButton: !isFinaly,
					buttonLoadingText: translate("Carregando"),
					saveButtonText: isCopy ? translate("Copiar") : translate("Salvar"),
					cancelButtonText: translate("Cancelar"),
					childrenOfStandardFooter: <>{isAdd && (
						<RCCButton color="blue" size="sm"
							onClick={() => {
								const adb001Codigo = watch("adb001Codigo");
								const adb001Titulo = watch("adb001Titulo");
								const adb001Descricao = watch("adb001Descricao");
								const adb001Taa001Id = convertToString(watch("adb001Taa001Id"));
								const adb001Taa005Id = convertToString(watch("adb001Taa005Id"));
								const adb001Ada001Id = convertToString(watch("adb001Ada001Id"));
								
								setSelectedAudit((prevState) => ({
									...prevState,
									adb001Codigo,
									adb001Titulo,
									adb001Descricao,
									adb001Taa001Id,
									adb001Taa005Id,
									adb001Ada001Id
								}));
								setFormSimplified((prevState) => !prevState);
							}}
						>
							{formSimplified ? translate("Ver cadastro completo") : translate("Ver cadastro simplificado")}
						</RCCButton>
					)}	
					</>
				}}
			>

				{formSimplified ? (
					<>
						<FormSimplifiedFields 
							control={control}
							isDisabled={false}
							isCopy={false}
							error={errors}
							register={register}
							audit={selectedAudit}
							onChangeStandard={onChangeStandard}
						/>
					</>
				): (
					<Tabs 
						name="tabAudit"
						tabInit={0}
						options={[
							{
								label: translate("Geral"),
								content: 
								<>
									<TabGeneral 
										control={control}
										watch={watch}
										disabled={isFinaly}
										error={errors}
										isAdd={isAdd}
										isCopy={isCopy}
										audit={selectedAudit}
										register={register}
										onChangeStandard={onChangeStandard}
									/>
								</>
							},{
								label: translate("Auditores"),
								content: (
									<Auditors 
										data={selectedAudit.adtbb002}
										control={control}
										disabled={isFinaly}
										register={register}
										clearErrors={clearErrors}
										error={errors}
										participants={{
											nameOfObject: "adtbb002",
											nameOfID: "adb002Id",
											nameOfSelectUser: "adb002Efa005Id",
											nameOfSelectUserDescription: "adb002Efa005Nome",
											nameOfFreeName: "adb002Nomeauditor",
											defaultValue: selectedAudit.adtbb002
										}}
									/>
								)
							},{
								label: translate("Participantes"),
								content: (
									<Participants 
										data={selectedAudit.adtbb003}
										control={control}
										disabled={isFinaly}
										register={register}
										clearErrors={clearErrors}
										error={errors}
										participants={{
											nameOfObject: "adtbb003",
											nameOfID: "adb003Id",
											nameOfSelectUser: "adb003Efa005Id",
											nameOfSelectUserDescription: "adb003Efa005Nome",
											nameOfFreeName: "adb003Nomeparticipante",
											defaultValue: selectedAudit.adtbb003
										}}
									/>
								)
							},
							{
								label: translate("Itens da Auditoria"),
								content: (
									<>
										{
											isFinaly 
												? 
												<>
													{
														selectedAudit.adtbb004?.map((item: any, index) => (
															<ItemAudited 
																key={v4()}
																item={item}
																index={index}
																disabled={isFinaly}
																control={control}
																register={register}
																errors={errors}
																setValue={setValue}
																fetchAttachments
															/>
														))
													}
												</>
												:
												<TabAuditItems 
													ref={refTabAuditItems}
													openModalStandardItems={openModalStandardItems}
													auditItems={selectedAudit.adtbb004 || []}
													control={control}
													disabled={false}
													register={register}
													unregister={unregister}
													error={errors}
													clearErrors={clearErrors}
												/>
										}
									</>
									
								)
							}
						]}
					/>
				)}

			</Modal>

			{/* Modal Delete */}
			<DeleteModal
				ref={modalDelete}
				title= {translate("Exclusão de Auditoria")}
				description= {`${translate("Deseja realmente excluir a auditoria")}: ${selectedAudit.adb001Titulo}?`}
				onSubmit = {handleSubmitDelete(submitDelete)} 
				height="250px"
			>
				<RCCGrid
					container
					item
					xs={10}
				>
					<RCCInput 
						register={registerDelete}
						name="adb001Titulo"
						label={translate("Digite o título da auditoria para excluí-lo")}
						maxLength={200}
						error={errorsDelete.adb001Titulo}
						required
					/>
				</RCCGrid>
			</DeleteModal>

			<StandardItensModal ref={refStandardItemsModal} returnFunction={refTabAuditItems.current?.addItemStandardItem} isImport/>
		</>
	);
};

export default forwardRef(ModalAudit);
